import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Drawer, IconButton, InputAdornment, MenuItem, Snackbar,
  Stack, TextField, Tooltip, Typography, useMediaQuery, useTheme,
} from "@mui/material";
import {
  Search, Filter, Timer, ChevronDown, ChevronRight, Maximize2, Minimize2, Rows3, Rows4, CheckSquare, X, Info, ChevronsLeftRight, ChevronsDownUp, ChevronsUpDown, Keyboard, Plus,
} from "lucide-react";
import { EmptyState, GlassCard, T, TYPE, formatDate, primaryButtonSx, ghostButtonSx } from "../../user/complaints/shared";
import { useSocketEvent } from "../../helpdesk/socket";
import { pm, errorMessage } from "../api";
import { usePm } from "../PmContext";
import { PersonPicker, PrioritySelect, Select, fieldSx } from "../inputs";
import IssueDetail, { IssueDetailModal } from "../IssueDetail";
import CreateIssueDialog from "../CreateIssueDialog";
import { FilterBuilder } from "../IssueNavigator";
import { BoardCard, CardMenu, QuickEdit } from "./BoardCard";
import { CompleteSprintDialog } from "./SprintDialogs";
import { PeopleFilter, MultiFilter, typeOptions, epicOptions, filterConditions, QuickFiltersDropdown } from "./BoardFilters";

const rankSort = (a, b) => (a.rank < b.rank ? -1 : a.rank > b.rank ? 1 : 0);

const laneOf = (issue, mode, ctx, epics, queryLanes) => {
  switch (mode) {
    case "assignee": return { key: String(issue.assignee?.id || "none"), label: issue.assignee?.name || "Unassigned", field: "assignee" };
    case "priority": { const p = ctx.priorityById.get(String(issue.priorityId)); return { key: String(issue.priorityId || "none"), sort: -(p?.level || 0), label: p?.name || "No priority", field: "priority" }; }
    case "epic": { const e = epics.find((x) => String(x._id) === String(issue.epicId)); return { key: String(issue.epicId || "none"), label: e ? `${e.key} ${e.summary}` : "No epic", field: "epic" }; }
    case "type": return { key: String(issue.typeId), label: ctx.typeById.get(String(issue.typeId))?.name || "Type" };
    case "story": case "parent": return { key: String(issue.parentId || "none"), label: issue.parentId ? `Sub-tasks of ${issue.parentKey || "parent"}` : "Issues without parent" };
    case "query": {
      const hit = (queryLanes || []).find((l) => l.issueIds.includes(String(issue._id)));
      return hit ? { key: String(hit._id), label: hit.name, sort: queryLanes.indexOf(hit) } : { key: "other", label: "Everything else", sort: 999 };
    }
    default: return { key: "all", label: "" };
  }
};

const useDebounced = (fn, ms = 350) => {
  const t = useRef(null);
  const f = useRef(fn);
  f.current = fn;
  return useCallback((...a) => {
    clearTimeout(t.current);
    t.current = setTimeout(() => f.current(...a), ms);
  }, [ms]);
};

/** Resolution / comment / assignee prompt when the workflow needs input (422 TRANSITION_REQUIREMENTS). */
const Requirements = ({ state, onCancel, onSubmit }) => {
  const ctx = usePm();
  const [resolution, setResolution] = useState("");
  const [comment, setComment] = useState("");
  const [assigneeId, setAssignee] = useState(null);
  useEffect(() => {
    setResolution(ctx.settings.resolutions[0] || "");
    setComment("");
  }, [state, ctx.settings.resolutions]);
  if (!state) return null;
  const v = state.validators || [];
  return (
    <Dialog open onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{state.transitionName || "Move issue"}</DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>{state.message}</Alert>
        <Stack spacing={2}>
          {v.some((x) => x.type === "required_resolution") && <Select label="Resolution" value={resolution} onChange={setResolution} options={ctx.settings.resolutions.map((r) => ({ value: r, label: r }))} />}
          {v.some((x) => x.type === "assignee_required") && <PersonPicker value={assigneeId} onChange={setAssignee} allowEmpty={false} />}
          <TextField multiline minRows={2} size="small" label={v.some((x) => x.type === "required_comment") ? "Comment *" : "Comment (optional)"} value={comment} onChange={(e) => setComment(e.target.value)} sx={fieldSx} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" sx={ghostButtonSx} onClick={onCancel}>Cancel</Button>
        <Button variant="contained" sx={primaryButtonSx} onClick={() => onSubmit({ resolution, comment, fields: assigneeId ? { assigneeId } : undefined })}>Move</Button>
      </DialogActions>
    </Dialog>
  );
};

/** Modal for adding a new board stage / column (Manager only). */
export const AddStageDialog = ({ open, onClose, board, onCreated }) => {
  const ctx = usePm();
  const [name, setName] = useState("");
  const [color, setColor] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const STAGE_COLORS = ["", "#0052CC", "#00875A", "#FFAB00", "#DE350B", "#6554C0", "#00B8D9", "#42526E"];

  useEffect(() => {
    if (open) {
      setName("");
      setColor("");
      setError("");
      setSubmitting(false);
    }
  }, [open]);

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setError("Stage name cannot be empty.");
      return;
    }
    // Check duplicate stage name against current board columns
    const existing = (board?.columns || []).some(
      (c) => c.name.trim().toLowerCase() === trimmed.toLowerCase()
    );
    if (existing) {
      setError(`A stage named "${trimmed}" already exists on this board.`);
      return;
    }

    try {
      setSubmitting(true);
      setError("");
      await pm.addColumn(board._id, { name: trimmed, color: color || null });
      ctx.notify(`Stage "${trimmed}" created successfully`, "success");
      onClose();
      if (onCreated) await onCreated();
      if (ctx.reload) ctx.reload();
    } catch (err) {
      setError(errorMessage(err) || "Failed to create stage");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "12px", p: 1 } }}>
      <form onSubmit={handleSubmit}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: "16px", pb: 1 }}>Add New Stage</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography sx={{ fontSize: TYPE.small, color: T.inkSecondary, mb: 2 }}>
            Add a new stage (Kanban column) to <b>{board?.name || "this board"}</b>. Issues can be dragged and dropped into this stage.
          </Typography>
          {error && <Alert severity="error" sx={{ mb: 2, fontSize: "13px" }}>{error}</Alert>}
          <TextField
            autoFocus
            label="Stage Name"
            placeholder="e.g. Code Review, QA, Ready to Deploy"
            value={name}
            onChange={(e) => { setName(e.target.value); if (error) setError(""); }}
            fullWidth
            size="small"
            required
            disabled={submitting}
            sx={{ ...fieldSx, mb: 2 }}
          />

          <Typography sx={{ fontSize: "12px", fontWeight: 600, color: T.inkSecondary, mb: 1 }}>
            Stage Accent Color
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            {STAGE_COLORS.map((cHex) => {
              const isSelected = (!cHex && !color) || color === cHex;
              return (
                <Box
                  key={cHex || "none"}
                  onClick={() => setColor(cHex)}
                  sx={{
                    cursor: "pointer",
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    bgcolor: cHex || "#E2E8F0",
                    border: isSelected ? "3px solid #0052CC" : "2px solid #DFE1E6",
                    boxShadow: isSelected ? "0 0 0 2px rgba(0,82,204,0.3)" : "none",
                    transition: "transform .1s",
                    "&:hover": { transform: "scale(1.15)" },
                  }}
                  title={cHex || "Default"}
                />
              );
            })}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="outlined" sx={ghostButtonSx} onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            sx={primaryButtonSx}
            disabled={submitting || !name.trim()}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <Plus size={16} />}
          >
            {submitting ? "Creating..." : "Create Stage"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export const BulkBar = ({ board, selected, clear, onDone, sprints, columns }) => {
  const ctx = usePm();
  const [op, setOp] = useState("");
  const [params, setParams] = useState({});
  const run = async () => {
    if (["delete", "archive"].includes(op) && !window.confirm(`${op === "delete" ? "Delete" : "Archive"} ${selected.length} issues?`)) return;
    try {
      const r = await pm.bulkOnBoard(board._id, selected, op, params);
      ctx.notify(`${r.succeeded} updated${r.failed ? `, ${r.failed} failed (${r.results.find((x) => !x.ok)?.error})` : ""}`, r.failed ? "warning" : "success");
      setOp("");
      setParams({});
      clear();
      onDone();
    } catch (err) {
      ctx.fail(err);
    }
  };
  const OPS = [["assign", "Assign"], ["transition", "Change status"], ["priority", "Priority"], ["labels", "Add labels"], ...(board.type === "scrum" ? [["sprint", "Sprint"]] : []), ["epic", "Epic"], ["flag", "Flag / unflag"], ["storyPoints", "Story points"], ["worklog", "Log work (each)"], ["watchers", "Watch"], ["archive", "Archive"], ["delete", "Delete"]];
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5, p: 1, bgcolor: T.brandSoft, borderRadius: "8px" }} flexWrap="wrap" useFlexGap>
      <Typography sx={{ fontSize: TYPE.small, fontWeight: 700 }}>{selected.length} selected</Typography>
      <TextField select size="small" label="Bulk action" value={op} onChange={(e) => { setOp(e.target.value); setParams(e.target.value === "watchers" ? { add: [String(ctx.me.id)] } : e.target.value === "flag" ? { flagged: true } : {}); }} sx={{ ...fieldSx, minWidth: 170 }}>
        {OPS.map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
      </TextField>
      {op === "assign" && <Box sx={{ minWidth: 200 }}><PersonPicker value={params.assigneeId} onChange={(id) => setParams({ assigneeId: id })} /></Box>}
      {op === "priority" && <Box sx={{ minWidth: 150 }}><PrioritySelect value={params.priorityId} onChange={(id) => setParams({ priorityId: id })} /></Box>}
      {op === "transition" && <Box sx={{ minWidth: 170 }}><Select label="Status" value={params.statusId} onChange={(id) => setParams({ statusId: id, resolution: ctx.settings.resolutions[0] })} options={columns.flatMap((c) => c.statusIds.map((s) => ({ value: s, label: `${c.name} · ${ctx.statusById.get(String(s))?.name}` })))} /></Box>}
      {op === "labels" && <TextField size="small" label="Labels (comma separated)" onChange={(e) => setParams({ add: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} sx={fieldSx} />}
      {op === "sprint" && <Box sx={{ minWidth: 170 }}><Select label="Sprint" value={params.sprintId} onChange={(id) => setParams({ sprintId: id })} allowEmpty emptyLabel="Backlog" options={sprints.map((s) => ({ value: s._id, label: s.name }))} /></Box>}
      {op === "epic" && <TextField size="small" label="Epic id or empty" onChange={(e) => setParams({ epicId: e.target.value || null })} sx={fieldSx} />}
      {op === "flag" && <Select label="" value={String(params.flagged)} onChange={(v) => setParams({ flagged: v === "true" })} options={[{ value: "true", label: "Flag" }, { value: "false", label: "Unflag" }]} />}
      {op === "storyPoints" && <TextField size="small" type="number" label="Points" onChange={(e) => setParams({ storyPoints: e.target.value === "" ? null : Number(e.target.value) })} sx={{ ...fieldSx, width: 100 }} />}
      {op === "worklog" && <TextField size="small" label="Time each (e.g. 30m)" onChange={(e) => setParams({ duration: e.target.value })} sx={fieldSx} />}
      <Button size="small" variant="contained" color={["delete", "archive"].includes(op) ? "error" : "primary"} disabled={!op} onClick={run}>Apply</Button>
      <Button size="small" onClick={clear}>Clear</Button>
    </Stack>
  );
};

/**
 * The board. Scrum: the active sprint (sprint selector when several run).
 * Kanban: continuous flow; the backlog column moves to the Backlog view when enabled.
 */
const BoardCanvas = ({ board, reloadBoard, prefs, savePrefs }) => {
  const ctx = usePm();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [sprintId, setSprintId] = useState(null);
  const [quick, setQuick] = useState([]);
  const [text, setText] = useState("");
  const [serverText, setServerText] = useState("");
  const [advanced, setAdvanced] = useState({ all: [], any: [], none: [] });
  const [showFilters, setShowFilters] = useState(false);
  const [pick, setPick] = useState({ people: [], types: [], epics: [] });
  const [members, setMembers] = useState([]);
  const [projectEpics, setProjectEpics] = useState([]);
  const [swimlane, setSwimlane] = useState(null);
  const [dragging, setDragging] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [requirements, setRequirements] = useState(null);
  const [wipAsk, setWipAsk] = useState(null);
  const [menu, setMenu] = useState(null);
  const [quickEdit, setQuickEdit] = useState(null);
  const [selecting, setSelecting] = useState(false);
  const [selected, setSelected] = useState([]);
  const [hovered, setHovered] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [subtaskOf, setSubtaskOf] = useState(null);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [undo, setUndo] = useState(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [components, setComponents] = useState(new Map());
  const [openSprints, setOpenSprints] = useState([]);
  const [addStageOpen, setAddStageOpen] = useState(false);
  const searchRef = useRef(null);
  const rootRef = useRef(null);
  const openKey = params.get("issue");
  const openAction = params.get("action");
  const project = board.project;
  const perms = board.permissions || [];
  const canMove = perms.includes("issue.transition") && !project.archivedAt;
  const compact = Boolean(prefs?.compact);
  const collapsedLanes = useMemo(() => new Set(prefs?.collapsedLanes || []), [prefs]);
  const collapsedCols = useMemo(() => new Set(prefs?.collapsedColumns || []), [prefs]);
  const lane = swimlane ?? board.swimlane ?? "none";

  const isManager = useMemo(() => {
    const storedRole = (localStorage.getItem("userRole") || localStorage.getItem("role") || "").toLowerCase().trim();
    const crmRole = (ctx?.me?.crmRole || ctx?.me?.role || "").toLowerCase().trim();
    const projectRole = (project?.myRoleKey || project?.myRole || project?.roleKey || "").toLowerCase().trim();
    const isAdmin = Boolean(ctx?.me?.isAdminRole);
    const MANAGER_ROLES = ["manager", "admin", "owner", "superadmin", "administrator"];
    const isMgr = MANAGER_ROLES.includes(storedRole) || MANAGER_ROLES.includes(crmRole) || MANAGER_ROLES.includes(projectRole) || isAdmin;
    const NON_MANAGER_ROLES = ["agent", "client_user", "client_manager", "developer", "viewer", "member", "guest"];
    if (NON_MANAGER_ROLES.includes(storedRole) && !isAdmin) return false;
    return isMgr;
  }, [ctx?.me, project]);

  const query = useMemo(() => {
    const clean = (l) => (l || []).filter((c) => c.field && c.op && (["exists", "not_exists"].includes(c.op) || (c.value !== "" && c.value !== undefined)));
    return { all: [...clean(advanced.all), ...filterConditions(pick)], any: clean(advanced.any), none: clean(advanced.none), ...(serverText ? { text: serverText } : {}) };
  }, [advanced, serverText, pick]);

  const load = useCallback(async () => {
    try {
      const d = await pm.boardIssues(board._id, { sprintId: sprintId || undefined, quick: quick.join(",") || undefined, query: JSON.stringify(query) });
      setData(d);
      setError("");
    } catch (err) {
      setError(errorMessage(err));
    }
  }, [board._id, sprintId, quick, query]);
  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    pm.components(project._id).then((r) => setComponents(new Map(r.map((c) => [String(c._id), c])))).catch(() => {});
    // people filter: this project's members + anyone with issues in it (still active in the workspace)
    Promise.all([pm.members(project._id).catch(() => []), pm.assignees(project._id).catch(() => [])]).then(([mem, asg]) => {
      const seen = new Set();
      const ids = [...(mem || []).map((m) => m.member?.id), ...(asg || []).map((a) => a.id)].map(String);
      setMembers(ids.map((id) => ctx.personById.get(id)).filter((p) => p && !seen.has(String(p.id)) && seen.add(String(p.id)))
        .sort((a, b) => (String(a.id) === String(ctx.me.id) ? -1 : String(b.id) === String(ctx.me.id) ? 1 : a.name.localeCompare(b.name))));
    });
    pm.epics(project._id).then((r) => setProjectEpics(r || [])).catch(() => {});
    if (board.type === "scrum") pm.boardSprints(board._id, { status: "active,future" }).then(setOpenSprints).catch(() => {});
  }, [project._id, board._id, board.type, ctx]);
  const reloadSoon = useDebounced(load, 400);

  // realtime: patch cards in place; reload only when something structural changed
  const findCard = (d, id) => d?.columns.flatMap((c) => c.issues).find((i) => String(i._id) === String(id));
  const placeCard = useCallback((patch) => {
    setData((d) => {
      if (!d) return d;
      const current = findCard(d, patch._id || patch.issueId);
      if (!current) {
        reloadSoon();
        return d;
      }
      const next = { ...current, ...Object.fromEntries(Object.entries(patch).filter(([k, v]) => v !== undefined && !["issueId", "boardId", "by", "columnId"].includes(k))) };
      const target = d.columns.find((c) => c.statusIds.map(String).includes(String(next.statusId)));
      const columns = d.columns.map((c) => {
        let issues = c.issues.filter((i) => String(i._id) !== String(next._id));
        if (target && String(c._id) === String(target._id)) issues = [...issues, next].sort(rankSort);
        const delta = issues.length - c.issues.length;
        return { ...c, issues, total: c.total + delta, wipCount: c.wipCount + delta, overWip: Boolean(c.wipLimit && c.wipCount + delta > c.wipLimit) };
      });
      if (!target) reloadSoon();
      return { ...d, columns };
    });
  }, [reloadSoon]);
  useSocketEvent("board.issue.moved", (p) => {
    if (String(p?.boardId) === String(board._id)) placeCard({ _id: p.issueId, statusId: p.statusId, statusCategory: p.statusCategory, rank: p.rank, rev: p.rev });
    else reloadSoon();
  });
  useSocketEvent(["issue.updated", "issue.assigned", "issue.status.changed"], (p) => {
    if (!p?._id) return;
    if (p.blockedChanged) return reloadSoon();
    placeCard({ _id: p._id, summary: p.summary, statusId: p.statusId ?? p.to, statusCategory: p.statusCategory, priorityId: p.priorityId, assignee: p.assignee, storyPoints: p.storyPoints, labels: p.labels, rank: p.rank, rev: p.rev, sprintId: p.sprintId });
  });
  useSocketEvent(["issue.created", "issue.deleted", "board.updated", "sprint.started", "sprint.completed", "sprint.updated"], () => reloadSoon());
  useSocketEvent("board.config.updated", (p) => {
    if (String(p?.boardId) === String(board._id)) {
      reloadBoard();
      reloadSoon();
    }
  });
  useSocketEvent("board.wip.exceeded", (p) => {
    if (String(p?.boardId) === String(board._id) && String(p?.by?.id) !== String(ctx.me.id)) ctx.notify(`WIP limit exceeded in ${p.column} (${p.count}/${p.limit})`, "warning");
  });

  const columns = useMemo(() => data?.columns || [], [data]);
  const epics = useMemo(() => data?.epics || [], [data]);
  const sprintsMap = useMemo(() => new Map(openSprints.map((s) => [String(s._id), s])), [openSprints]);

  // instant client-side search over loaded cards (key, summary, people, labels, components, epic)
  const matchText = useCallback((i) => {
    const q = text.trim().toLowerCase();
    if (!q) return true;
    const epic = epics.find((e) => String(e._id) === String(i.epicId));
    const hay = [i.key, i.summary, i.assignee?.name, i.reporter?.name, ...(i.labels || []), ...(i.componentIds || []).map((c) => components.get(String(c))?.name), epic?.summary, epic?.key].filter(Boolean).join(" ").toLowerCase();
    return hay.includes(q);
  }, [text, epics, components]);

  const lanes = useMemo(() => {
    const map = new Map();
    for (const i of columns.flatMap((c) => c.issues)) {
      const l = laneOf(i, lane, ctx, epics, data?.lanes);
      if (!map.has(l.key)) map.set(l.key, l);
    }
    const list = [...map.values()].sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0) || a.label.localeCompare(b.label));
    return list.length ? list : [{ key: "all", label: "" }];
  }, [columns, lane, ctx, epics, data?.lanes]);

  // ---------------------------------------------------------------------------
  const doMove = async (issue, column, { beforeId, afterId, laneTo, extra = {} } = {}) => {
    const prev = data;
    const fromCol = columns.find((c) => c.statusIds.map(String).includes(String(issue.statusId)));
    const siblings = fromCol ? fromCol.issues : [];
    const idx = siblings.findIndex((i) => i._id === issue._id);
    // optimistic placement
    setData((d) => {
      if (!d) return d;
      const cols = d.columns.map((c) => ({ ...c, issues: c.issues.filter((i) => i._id !== issue._id) }));
      const t = cols.find((c) => c._id === column._id);
      const at = beforeId ? t.issues.findIndex((i) => i._id === beforeId) : t.issues.length;
      t.issues.splice(at < 0 ? t.issues.length : at, 0, { ...issue, statusId: column.statusIds.map(String).includes(String(issue.statusId)) ? issue.statusId : column.statusIds[0] });
      return { ...d, columns: cols };
    });
    try {
      const body = { issueId: issue._id, columnId: column._id, beforeId, afterId, rev: issue.rev, ...(laneTo ? { lane: laneTo } : {}), ...extra };
      const r = await pm.moveCard(board._id, body);
      setRequirements(null);
      setWipAsk(null);
      if (r.warnings?.length) ctx.notify(r.warnings.join(" · "), "warning");
      if (fromCol && String(fromCol._id) !== String(column._id)) {
        setUndo({ issue: { ...issue, rev: r.data.rev, statusId: r.data.statusId }, column: fromCol, beforeId: siblings[idx + 1]?._id, afterId: siblings[idx - 1]?._id, text: `${issue.key} moved to ${column.name}` });
      }
      load();
    } catch (err) {
      setData(prev);
      const res = err?.response?.data;
      const code = res?.code;
      if (err?.response?.status === 422 && code === "TRANSITION_REQUIREMENTS") {
        setRequirements({ issue, column, beforeId, afterId, laneTo, message: res.message, validators: res.details?.transition?.validators, transitionName: res.details?.transition?.name });
      } else if (code === "WIP_CONFIRM") {
        setWipAsk({ issue, column, beforeId, afterId, laneTo, extra, message: res.message });
      } else if (code === "CONFLICT") {
        ctx.notify(res.message, "warning");
        load();
      } else ctx.fail(err, "Could not move the card");
    }
  };

  const onDrop = (e, column, laneKey) => {
    e.preventDefault();
    setDropTarget(null);
    const issue = dragging;
    setDragging(null);
    if (!issue) return;
    const cards = [...e.currentTarget.querySelectorAll("[data-card]")].filter((el) => el.dataset.card !== issue._id);
    const before = cards.find((el) => { const r = el.getBoundingClientRect(); return e.clientY < r.top + r.height / 2; });
    const beforeId = before?.dataset.card;
    const inCol = column.issues.filter((i) => i._id !== issue._id);
    const afterId = beforeId ? inCol[inCol.findIndex((i) => i._id === beforeId) - 1]?._id : inCol[inCol.length - 1]?._id;
    const fromLane = laneOf(issue, lane, ctx, epics, data?.lanes);
    const laneTo = fromLane.field && laneKey !== fromLane.key ? { field: fromLane.field, value: laneKey } : null;
    const sameColumn = column.statusIds.map(String).includes(String(issue.statusId));
    if (sameColumn && !laneTo && ((!beforeId && !afterId) || board.ranking?.enabled === false)) return;
    doMove(issue, column, { beforeId, afterId, laneTo });
  };

  // quick actions ------------------------------------------------------------------
  const setIssueParam = (key, action) => {
    const n = new URLSearchParams(params);
    if (key) n.set("issue", key);
    else n.delete("issue");
    if (action) n.set("action", action);
    else n.delete("action");
    setParams(n, { replace: !key });
  };
  const update = async (issue, patch, msg) => {
    try {
      await pm.updateIssue(issue._id, { ...patch, rev: issue.rev });
      if (msg) ctx.notify(msg);
      load();
    } catch (err) {
      if (err?.response?.status === 409) load();
      ctx.fail(err);
    }
  };
  const act = async (a, issue, arg) => {
    try {
      switch (a) {
        case "open": return setIssueParam(issue.key, arg);
        case "column": return doMove(issue, arg, {});
        case "assignMe": return update(issue, { assigneeId: String(ctx.me.id) }, `${issue.key} assigned to you`);
        case "assign": return setQuickEdit({ issue, kind: "person", title: "Assign", initial: issue.assignee?.id ? String(issue.assignee.id) : null, apply: (v) => update(issue, { assigneeId: v || null }) });
        case "priority": return update(issue, { priorityId: arg });
        case "points": return setQuickEdit({ issue, kind: "number", title: "Story points", label: "Points", initial: issue.storyPoints ?? "", apply: (v) => update(issue, { storyPoints: v === "" ? null : Number(v) }) });
        case "due": return setQuickEdit({ issue, kind: "date", title: "Due date", label: "Due", initial: issue.dueDate ? new Date(issue.dueDate).toISOString().slice(0, 10) : "", apply: (v) => update(issue, { dueDate: v || null }) });
        case "label": return setQuickEdit({ issue, kind: "text", title: "Add label", label: "Label", placeholder: "email", apply: (v) => v && update(issue, { labels: [...(issue.labels || []), v] }) });
        case "flag":
          if (issue.flagged) return update(issue, { flagged: false, flagReason: "" }, `${issue.key} unflagged`);
          return setQuickEdit({ issue, kind: "text", title: "Flag as impediment", label: "Reason (optional)", apply: (v) => update(issue, { flagged: true, flagReason: v }, `${issue.key} flagged`) });
        case "sprint": return update(issue, { sprintId: arg }, arg ? `${issue.key} added to sprint` : `${issue.key} removed from the sprint`);
        case "comment": return setQuickEdit({ issue, kind: "multiline", title: "Add comment", label: "Comment", apply: async (v) => { if (!v.trim()) return; await pm.addComment(issue._id, { body: `<p>${v.replace(/[<>&]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;" }[c])).replace(/\n/g, "<br>")}</p>` }); ctx.notify("Comment added"); load(); } });
        case "worklog": return setQuickEdit({ issue, kind: "text", title: "Log work", label: "Time spent", placeholder: "1h 30m", extraLabel: "What did you work on?", apply: async (v, d) => { await pm.addWorklog(issue._id, { duration: v, description: d }); ctx.notify(`Logged ${v} on ${issue.key}`); load(); } });
        case "watch": await pm.watch(issue._id, true); return ctx.notify(`Watching ${issue.key}`);
        case "subtask": return setSubtaskOf(issue);
        case "clone": { const c = await pm.cloneIssue(issue._id, {}); ctx.notify(`Cloned as ${c.key}`); return load(); }
        case "archive": await pm.archiveIssue(issue._id); ctx.notify(`${issue.key} archived`); return load();
        case "delete": if (!window.confirm(`Delete ${issue.key}? An admin can restore it.`)) return undefined; await pm.deleteIssue(issue._id); ctx.notify(`${issue.key} deleted`); return load();
        default: return undefined;
      }
    } catch (err) {
      return ctx.fail(err);
    }
  };

  // keyboard shortcuts (C create, / search, E open, A assign, S status, M sprint) — disable in board prefs
  useEffect(() => {
    if (prefs?.shortcuts === false) return undefined;
    const onKey = (e) => {
      const tag = document.activeElement?.tagName;
      if (["INPUT", "TEXTAREA", "SELECT"].includes(tag) || document.activeElement?.isContentEditable || e.ctrlKey || e.metaKey || e.altKey) return;
      if (document.querySelector("[role=dialog], [role=presentation].MuiDrawer-root")) return;
      const k = e.key.toLowerCase();
      if (k === "c" && perms.includes("issue.create")) { e.preventDefault(); setCreateOpen(true); }
      else if (e.key === "/") { e.preventDefault(); searchRef.current?.focus(); }
      else if (hovered && k === "e") act("open", hovered);
      else if (hovered && k === "a") act("assign", hovered);
      else if (hovered && k === "s") { const el = document.querySelector(`[data-card="${hovered._id}"]`); if (el) setMenu({ el, issue: hovered }); }
      else if (hovered && k === "m" && board.type === "scrum") { const el = document.querySelector(`[data-card="${hovered._id}"]`); if (el) setMenu({ el, issue: hovered }); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await rootRef.current?.requestFullscreen?.();
        setFullscreen(true);
      } else {
        await document.exitFullscreen();
        setFullscreen(false);
      }
    } catch {
      setFullscreen((f) => !f);
    }
  };
  const toggleLane = (k) => savePrefs({ collapsedLanes: collapsedLanes.has(k) ? [...collapsedLanes].filter((x) => x !== k) : [...collapsedLanes, k] });
  const toggleCol = (k) => savePrefs({ collapsedColumns: collapsedCols.has(k) ? [...collapsedCols].filter((x) => x !== k) : [...collapsedCols, k] });
  const loadMore = async (c) => {
    try {
      const r = await pm.boardIssues(board._id, { columnId: c._id, offset: c.issues.length, sprintId: data?.sprint?._id, quick: quick.join(",") || undefined, query: JSON.stringify(query) });
      const page = r.columns[0];
      setData((d) => ({ ...d, columns: d.columns.map((x) => (x._id === c._id ? { ...x, issues: [...x.issues, ...page.issues.filter((i) => !x.issues.some((y) => y._id === i._id))], hasMore: page.hasMore } : x)) }));
    } catch (err) {
      ctx.fail(err);
    }
  };

  if (error && !data) return <Alert severity="error">{error}</Alert>;
  if (!data) return <Box sx={{ py: 8, textAlign: "center" }}><CircularProgress size={24} /></Box>;

  const filterCount = (advanced.all?.length || 0) + (advanced.any?.length || 0) + (advanced.none?.length || 0);
  // a quick filter that only picks an issue type duplicates the Type dropdown (older boards still have Bugs/Stories/Tasks)
  const typeOnly = (q) => !q.query?.any?.length && !q.query?.none?.length && q.query?.all?.length === 1 && q.query.all[0].field === "type";
  const quickFilters = (board.quickFilters || []).filter((q) => !typeOnly(q));
  const pickCount = pick.people.length + pick.types.length + pick.epics.length;
  const setPickKey = (k) => (v) => setPick((p) => ({ ...p, [k]: v }));
  const colWidth = compact ? 220 : 272;
  const daysLeft = data.sprint?.endDate ? Math.ceil((new Date(data.sprint.endDate) - Date.now()) / 86400000) : null;

  const getColSx = (collapsed) => ({
    width: collapsed ? 44 : colWidth,
    flex: collapsed ? "0 0 44px" : "1 1 280px",
    minWidth: collapsed ? 44 : 260,
    maxWidth: collapsed ? 44 : (columns.length <= 3 ? 420 : 360),
    flexShrink: 0,
    scrollSnapAlign: "start",
  });

  const toolbar = (
    <Stack
      direction={{ xs: "column", md: "row" }}
      spacing={1}
      alignItems={{ md: "center" }}
      sx={{ mb: 2, minHeight: 40 }}
      flexWrap="wrap"
      useFlexGap
    >
      <TextField
        inputRef={searchRef}
        size="small"
        placeholder="Search board…  ( / )"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") setServerText(text.trim());
          if (e.key === "Escape") {
            setText("");
            setServerText("");
          }
        }}
        sx={{ ...fieldSx, width: { xs: "100%", md: 200 } }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search size={14} color="#6B778C" />
            </InputAdornment>
          ),
          endAdornment: serverText ? (
            <Tooltip title="Also searching descriptions & comments on the server — Esc to clear">
              <Chip size="small" label="+ descriptions" onDelete={() => setServerText("")} sx={{ height: 20 }} />
            </Tooltip>
          ) : null,
        }}
      />
      {members.length > 0 && (
        <PeopleFilter people={members} value={pick.people} onChange={setPickKey("people")} />
      )}
      <MultiFilter
        label="Epic"
        options={epicOptions(projectEpics)}
        value={pick.epics}
        onChange={setPickKey("epics")}
        emptyText="No epics in this project"
      />
      <MultiFilter
        label="Type"
        options={typeOptions(ctx, project)}
        value={pick.types}
        onChange={setPickKey("types")}
      />
      {quickFilters.length > 0 && (
        <QuickFiltersDropdown
          filters={quickFilters}
          activeIds={quick}
          onChange={setQuick}
          onClear={() => setQuick([])}
        />
      )}
      {(pickCount > 0 || quick.length > 0 || filterCount > 0 || text) && (
        <Button
          size="small"
          sx={{ color: T.inkSecondary, textTransform: "none", fontSize: "13px" }}
          onClick={() => {
            setPick({ people: [], types: [], epics: [] });
            setQuick([]);
            setAdvanced({ all: [], any: [], none: [] });
            setText("");
            setServerText("");
          }}
        >
          Clear filters
        </Button>
      )}

      <Box sx={{ flexGrow: 1 }} />

      {/* Right-Side Board Toolbar Actions */}
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        {isManager && (
          <Button
            size="small"
            variant="contained"
            startIcon={<Plus size={15} />}
            onClick={() => setAddStageOpen(true)}
            sx={{
              ...primaryButtonSx,
              textTransform: "none",
              fontWeight: 600,
              fontSize: "13px",
              height: 36,
              whiteSpace: "nowrap",
              px: 1.5,
              borderRadius: "6px",
            }}
          >
            Add Stage
          </Button>
        )}
        <TextField
          select
          size="small"
          label="Swimlanes"
          value={lane}
          onChange={(e) => setSwimlane(e.target.value)}
          sx={{ ...fieldSx, minWidth: 120 }}
        >
          {[
            ["none", "None"],
            ["assignee", "Assignee"],
            ["priority", "Priority"],
            ["epic", "Epic"],
            ["type", "Issue type"],
            ["parent", "Parent / story"],
            ...(board.swimlaneQueries?.length ? [["query", "Queries"]] : []),
          ].map(([v, l]) => (
            <MenuItem key={v} value={v}>
              {l}
            </MenuItem>
          ))}
        </TextField>
        <Button
          size="small"
          variant={filterCount ? "contained" : "outlined"}
          sx={{
            ...(filterCount ? primaryButtonSx : ghostButtonSx),
            height: 36,
            textTransform: "none",
            fontSize: "13px",
          }}
          startIcon={<Filter size={14} />}
          onClick={() => setShowFilters(true)}
        >
          Filters{filterCount ? ` (${filterCount})` : ""}
        </Button>
        <Stack direction="row" spacing={0.25} alignItems="center">
          {lane !== "none" && (
            <Tooltip title="Expand all lanes">
              <IconButton size="small" onClick={() => savePrefs({ collapsedLanes: [] })}>
                <ChevronsUpDown size={16} />
              </IconButton>
            </Tooltip>
          )}
          {lane !== "none" && (
            <Tooltip title="Collapse all lanes">
              <IconButton
                size="small"
                onClick={() => savePrefs({ collapsedLanes: lanes.map((l) => l.key) })}
              >
                <ChevronsDownUp size={16} />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={compact ? "Comfortable cards" : "Compact cards"}>
            <IconButton size="small" onClick={() => savePrefs({ compact: !compact })}>
              {compact ? <Rows3 size={16} /> : <Rows4 size={16} />}
            </IconButton>
          </Tooltip>
          <Tooltip title={selecting ? "Stop selecting" : "Select cards (or Shift+click)"}>
            <IconButton
              size="small"
              color={selecting ? "primary" : "default"}
              onClick={() => {
                setSelecting((s) => !s);
                setSelected([]);
              }}
            >
              <CheckSquare size={16} />
            </IconButton>
          </Tooltip>
          <Tooltip title={fullscreen ? "Exit full screen" : "Full screen"}>
            <IconButton size="small" onClick={toggleFullscreen}>
              {fullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
            </IconButton>
          </Tooltip>
          <Tooltip
            title={
              prefs?.shortcuts === false
                ? "Keyboard shortcuts are off — click to enable"
                : "Shortcuts: C create · / search · hover a card: E open · A assign · S status · M sprint (click to disable)"
            }
          >
            <IconButton
              size="small"
              color={prefs?.shortcuts === false ? "default" : "primary"}
              onClick={() => savePrefs({ shortcuts: prefs?.shortcuts === false })}
            >
              <Keyboard size={16} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
    </Stack>
  );

  const sprintBar = board.type === "scrum" && data.sprint && (
    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} alignItems={{ sm: "center" }} sx={{ mb: 1.5, p: 1.25, borderRadius: "10px", bgcolor: T.surfaceMuted, border: `1px solid ${T.border}` }}>
      <Timer size={16} color={T.brand} />
      {data.sprints.length > 1 ? (
        <TextField select size="small" value={data.sprint._id} onChange={(e) => setSprintId(e.target.value)} sx={{ ...fieldSx, minWidth: 180 }}>
          {data.sprints.map((s) => <MenuItem key={s._id} value={s._id}>{s.name}</MenuItem>)}
        </TextField>
      ) : <Typography sx={{ fontWeight: 700, fontSize: TYPE.small }}>{data.sprint.name}</Typography>}
      {data.sprint.goal && <Typography sx={{ fontSize: TYPE.small, color: T.inkSecondary }} noWrap>Goal: {data.sprint.goal}</Typography>}
      <Box sx={{ flexGrow: 1 }} />
      {data.sprint.endDate && <Typography sx={{ fontSize: TYPE.micro, color: daysLeft < 0 ? "#B42318" : T.inkMuted }}>{formatDate(data.sprint.startDate)} – {formatDate(data.sprint.endDate)} · {daysLeft < 0 ? `${-daysLeft}d overdue` : `${daysLeft}d left`}</Typography>}
      {perms.includes("sprint.complete") && ctx.me.isAdminRole && !project.archivedAt && <Button size="small" variant="contained" sx={primaryButtonSx} onClick={() => setCompleteOpen(true)}>Complete sprint</Button>}
    </Stack>
  );

  return (
    <Box ref={rootRef} sx={{ bgcolor: "#fff", ...(fullscreen ? { position: "fixed", inset: 0, zIndex: 1300, p: 2, overflow: "auto" } : {}) }}>
      {toolbar}
      {sprintBar}
      {selected.length > 0 && <BulkBar board={board} selected={selected} clear={() => { setSelected([]); setSelecting(false); }} onDone={load} sprints={openSprints} columns={columns} />}
      {data.unmapped > 0 && <Alert severity="warning" sx={{ mb: 1.5 }} action={board.canManage && <Button size="small" component={RouterLink} to={`/projects/${project.key}/boards/${board._id}/settings?section=columns`}>Map statuses</Button>}>{data.unmapped} issue(s) are in a status no column shows.</Alert>}
      {board.type === "scrum" && !data.sprint ? (
        <GlassCard>
          <EmptyState icon={Timer} title="No active sprint on this board" description={data.futureSprints?.length ? `Start ${data.futureSprints[0].name} from the backlog to see its issues here.` : "Create a sprint in the backlog, plan issues into it and start it."}
            action={<Button variant="contained" sx={primaryButtonSx} component={RouterLink} to={`/projects/${project.key}/boards/${board._id}/backlog`}>Go to backlog</Button>} />
        </GlassCard>
      ) : (
        <Box sx={{ overflowX: "auto", pb: 2, scrollSnapType: mobile ? "x mandatory" : "none" }}>
          {/* column headers */}
          <Box sx={{ display: "flex", gap: 1.5, minWidth: "min-content", mb: 1 }}>
            {columns.map((c) => {
              const collapsed = collapsedCols.has(String(c._id));
              const hasPolicy = c.policy?.entry || c.policy?.exit;
              return (
                <Box key={c._id} sx={getColSx(collapsed)}>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{
                      height: 40,
                      minHeight: 40,
                      maxHeight: 40,
                      px: 1.25,
                      bgcolor: c.overWip ? "#FEE4E2" : c.atWip ? "#FEF0C7" : "#F4F5F7",
                      borderRadius: "6px",
                      border: `1px solid ${c.overWip ? "#FDA29B" : "#DFE1E6"}`,
                      borderTop: c.color ? `3px solid ${c.color}` : `1px solid ${c.overWip ? "#FDA29B" : "#DFE1E6"}`,
                      boxSizing: "border-box",
                    }}
                  >
                    <IconButton
                      size="small"
                      sx={{ p: 0.25, color: "#6B778C" }}
                      onClick={() => toggleCol(String(c._id))}
                      aria-label={collapsed ? "Expand column" : "Collapse column"}
                    >
                      {collapsed ? <ChevronsLeftRight size={14} /> : <ChevronDown size={14} />}
                    </IconButton>
                    {!collapsed && (
                      <Typography
                        sx={{
                          fontSize: "12px",
                          fontWeight: 700,
                          textTransform: "uppercase",
                          letterSpacing: "0.04em",
                          color: "#5E6C84",
                          flexGrow: 1,
                        }}
                        noWrap
                      >
                        {c.name}
                      </Typography>
                    )}
                    {!collapsed && hasPolicy && (
                      <Tooltip
                        title={
                          <Box>
                            {c.policy.entry && <div><b>Entry:</b> {c.policy.entry}</div>}
                            {c.policy.exit && <div><b>Exit:</b> {c.policy.exit}</div>}
                            {c.policy.enforce && <div><i>Enforced</i></div>}
                          </Box>
                        }
                      >
                        <Info size={13} color="#6B778C" />
                      </Tooltip>
                    )}
                    <Tooltip
                      title={`${c.wipCount} in column${c.wipLimit ? ` · WIP limit ${c.wipLimit}` : ""}${c.minLimit ? ` · minimum ${c.minLimit}` : ""}${c.estimate ? ` · ${c.estimate} ${board.estimationConfig?.unit === "storyPoints" ? "pts" : ""}` : ""}`}
                    >
                      <Box
                        sx={{
                          bgcolor: c.overWip ? "#FDA29B" : c.underMin ? "#FEDF89" : "#DFE1E6",
                          color: c.overWip ? "#B42318" : c.underMin ? "#B54708" : "#172B4D",
                          fontSize: "11px",
                          fontWeight: 700,
                          px: 0.8,
                          py: 0.2,
                          borderRadius: "10px",
                          minWidth: 20,
                          textAlign: "center",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {c.wipLimit ? `${c.wipCount}/${c.wipLimit}` : c.total}
                      </Box>
                    </Tooltip>
                  </Stack>
                </Box>
              );
            })}
          </Box>
          {lanes.map((l) => {
            const laneCollapsed = lane !== "none" && collapsedLanes.has(l.key);
            const laneCount = columns.reduce((s, c) => s + c.issues.filter((i) => lane === "none" || laneOf(i, lane, ctx, epics, data.lanes).key === l.key).length, 0);
            return (
              <Box key={l.key} sx={{ mt: lane !== "none" ? 1.5 : 0, minWidth: "min-content" }}>
                {lane !== "none" && (
                  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1, px: 0.5, cursor: "pointer" }} onClick={() => toggleLane(l.key)}>
                    {laneCollapsed ? <ChevronRight size={15} /> : <ChevronDown size={15} />}
                    <Typography sx={{ fontSize: TYPE.small, fontWeight: 700 }}>{l.label}</Typography>
                    <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>{laneCount}</Typography>
                  </Stack>
                )}
                {!laneCollapsed && (
                  <Box sx={{ display: "flex", gap: 1.5 }}>
                    {columns.map((c) => {
                      const collapsed = collapsedCols.has(String(c._id));
                      const cards = c.issues.filter((i) => (lane === "none" || laneOf(i, lane, ctx, epics, data.lanes).key === l.key) && matchText(i));
                      const target = dropTarget === `${c._id}:${l.key}`;
                      return (
                        <Box key={c._id}
                          onDragOver={(e) => { if (canMove && dragging) { e.preventDefault(); setDropTarget(`${c._id}:${l.key}`); } }}
                          onDragLeave={() => setDropTarget(null)} onDrop={(e) => onDrop(e, c, l.key)}
                          sx={{
                            ...getColSx(collapsed),
                            minHeight: 180,
                            maxHeight: "calc(100vh - 270px)",
                            overflowY: "auto",
                            p: collapsed ? 0.5 : 1,
                            borderRadius: "8px",
                            bgcolor: target ? T.brandSoft : "#F4F5F7",
                            border: `1px ${target ? "dashed " + T.brand : "solid #EBECF0"}`,
                            transition: "background .12s, border-color .12s",
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                            boxSizing: "border-box",
                            "&::-webkit-scrollbar": { width: 5 },
                            "&::-webkit-scrollbar-thumb": { bgcolor: "rgba(9, 30, 66, 0.15)", borderRadius: 3 },
                          }}>
                          {collapsed ? (
                            <Typography sx={{ writingMode: "vertical-rl", fontSize: TYPE.micro, color: T.inkMuted, mx: "auto", my: 1 }}>{c.name} · {cards.length}</Typography>
                          ) : (
                            <>
                              {cards.map((i) => (
                                <Box key={i._id} data-card={i._id} sx={{ width: "100%", opacity: dragging?._id === i._id ? 0.4 : 1, transition: "opacity .12s" }}>
                                  <BoardCard issue={i} board={board} epics={epics} components={components} sprints={sprintsMap} compact={compact}
                                    selecting={selecting} selected={selected.includes(i._id)} onSelect={(on) => { setSelecting(true); setSelected((s) => (on ? [...new Set([...s, i._id])] : s.filter((x) => x !== i._id))); }}
                                    onOpen={(x) => setIssueParam(x.key)} onMenu={(el, x) => setMenu({ el, issue: x })} onHover={setHovered} overWip={c.overWip}
                                    draggable={canMove && !mobile} onDragStart={(e) => { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", i._id); setDragging(i); }} />
                                </Box>
                              ))}
                              {c.hasMore && lane === "none" && <Button fullWidth size="small" onClick={() => loadMore(c)}>Load more ({c.total - c.issues.length})</Button>}
                            </>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      )}

      <CardMenu anchor={menu?.el} issue={menu?.issue} board={board} columns={columns} sprints={openSprints} perms={perms} onClose={() => setMenu(null)} act={act} />
      <QuickEdit state={quickEdit} onClose={() => setQuickEdit(null)} onSubmit={async (v, extra) => { const s = quickEdit; setQuickEdit(null); await s.apply(v, extra); }} />
      <Requirements state={requirements} onCancel={() => setRequirements(null)} onSubmit={(input) => doMove(requirements.issue, requirements.column, { beforeId: requirements.beforeId, afterId: requirements.afterId, laneTo: requirements.laneTo, extra: input })} />
      <Dialog open={Boolean(wipAsk)} onClose={() => setWipAsk(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Exceed the WIP limit?</DialogTitle>
        <DialogContent dividers><Typography sx={{ fontSize: TYPE.small }}>{wipAsk?.message}</Typography></DialogContent>
        <DialogActions><Button onClick={() => setWipAsk(null)}>Cancel</Button><Button variant="contained" color="warning" onClick={() => doMove(wipAsk.issue, wipAsk.column, { beforeId: wipAsk.beforeId, afterId: wipAsk.afterId, laneTo: wipAsk.laneTo, extra: { ...wipAsk.extra, confirmWip: true } })}>Move anyway</Button></DialogActions>
      </Dialog>
      <Drawer anchor="right" open={showFilters} onClose={() => setShowFilters(false)} PaperProps={{ sx: { width: { xs: "100%", sm: 640 }, p: 2 } }}>
        <Stack direction="row" alignItems="center" sx={{ mb: 1.5 }}>
          <Typography sx={{ fontWeight: 700, flexGrow: 1 }}>Board filters</Typography>
          <Button size="small" onClick={() => setAdvanced({ all: [], any: [], none: [] })}>Reset</Button>
          <IconButton onClick={() => setShowFilters(false)}><X size={18} /></IconButton>
        </Stack>
        <Typography sx={{ fontSize: TYPE.micro, color: T.inkSecondary, mb: 1 }}>Only for you — the board and other people's views are not changed. WIP counts always use the whole column.</Typography>
        <FilterBuilder query={advanced} onChange={setAdvanced} sprints={openSprints} />
      </Drawer>
      <IssueDetailModal
        issueKey={openKey}
        open={Boolean(openKey)}
        initialAction={openAction}
        onClose={() => setIssueParam(null)}
        onOpenIssue={(key) => setIssueParam(key)}
        onChanged={load}
      />
      <CreateIssueDialog open={createOpen} onClose={() => setCreateOpen(false)} project={project} defaults={data.sprint ? { sprintId: data.sprint._id } : {}} onCreated={() => { setCreateOpen(false); load(); }} />
      <CreateIssueDialog open={Boolean(subtaskOf)} onClose={() => setSubtaskOf(null)} project={project}
        defaults={subtaskOf ? { parentId: subtaskOf._id, typeId: ctx.issueTypes.find((t) => t.level === 2 && (t.allowedParentTypeIds || []).map(String).includes(String(subtaskOf.typeId)))?._id, sprintId: subtaskOf.sprintId || undefined } : {}}
        onCreated={() => { setSubtaskOf(null); load(); }} />
      {completeOpen && <CompleteSprintDialog sprint={data.sprint} onClose={() => setCompleteOpen(false)} onDone={() => { setCompleteOpen(false); load(); }} />}
      <Snackbar open={Boolean(undo)} autoHideDuration={6000} onClose={() => setUndo(null)} message={undo?.text}
        action={<Button color="inherit" size="small" onClick={() => { const u = undo; setUndo(null); doMove(u.issue, u.column, { beforeId: u.beforeId, afterId: u.afterId }); }}>Undo</Button>} />
      <AddStageDialog open={addStageOpen} onClose={() => setAddStageOpen(false)} board={board} onCreated={async () => { if (reloadBoard) await reloadBoard(); await load(); }} />
    </Box>
  );
};

export default BoardCanvas;
